//@ts-ignore
//@ts-nocheck
/* eslint-disable @typescript-eslint/no-unused-expressions, no-debugger, no-console, prefer-const, prefer-rest-params,  no-sequences */

import useTracker from "../../useTrackerContext";
import color from "context/MaterialUITheme/color";
import Typography from "@mui/material/Typography";
import CTrackerTimelineItem, {
  trackerStatusColor,
} from "components/CTimelineItem";
import API from "utils/API";
import moment from "moment";
import CButton from "components/CButton";
import CDialog from "components/CDialog";
import isHolliday from "utils/isHolliday";
import { Close } from "@mui/icons-material";
import CCalendar from "components/CCalendar";
import NotarizeLogo from "./NotarizeLogo.svg";
import { Box, IconButton } from "@mui/material";
import addWorkingDays from "utils/addWorkingDays";
import { useCallback, useEffect, useMemo, useState } from "react";
import useUser from "context/UserCustomer/useUserCustomerContext";

const ScheduleClosingVerification: React.FC = () => {
  const { loan, type } = useTracker();
  const isBorrower = type === "borrower";
  const { user } = useUser();
  const [showNPS, setShowNPS] = useState(false);
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const step = isBorrower ? loan?.borrowerSteps : loan?.coborrowerSteps;
  const status = step?.scheduleClosingVerification?.status ?? "pending";
  const message = step?.scheduleClosingVerification?.message ?? "";
  const disclosuresStatus = step?.disclosuresVerification?.status ?? "pending";
  const [schedulingDate, setSchedulingDate] = useState(false);
  const [needManualAppraisal, setNeedManualAppraisal] = useState(false);

  const [confirm, setConfirm] = useState(false);
  const handleSelectConfirm = async () => {
    setCalendarIsOpen(false);
    setConfirm(true);
  };

  const daysFrom = loan?.loanType === "HELOC" ? 2 : 5;
  const daysTo = loan?.loanType === "HELOC" ? 9 : 12;

  const rangeFrom = useMemo(
    () =>
      addWorkingDays(
        moment(loan?.borrowerSteps?.creditVerification.updatedAt),
        daysFrom
      ).toDate(),
    [loan?.borrowerSteps?.creditVerification.updatedAt, daysFrom]
  );
  const rangeTo = useMemo(
    () =>
      addWorkingDays(
        moment(loan?.borrowerSteps?.creditVerification.updatedAt),
        daysTo
      ).toDate(),
    [loan?.borrowerSteps?.creditVerification.updatedAt, daysTo]
  );

  const disableSubmit = useMemo(() => {
    if (
      selectedDate &&
      moment(selectedDate)?.isBetween(moment(rangeFrom), moment(rangeTo)) &&
      !isHolliday(selectedDate)
    )
      return false;
    return true;
  }, [rangeFrom, rangeTo, selectedDate]);

  const scheduleDate = useCallback(async () => {
    if (!selectedDate) return undefined;
    setSchedulingDate(true);
    const response = await API.post({
      url: `/schedule-closing-date`,
      data: {
        date: selectedDate.toISOString(),
      },
    });
    if ("error" in response) {
      alert(response.error);
    } else {
      setShowNPS(true);
      setCalendarIsOpen(false);
      setConfirm(false);
    }
    setSchedulingDate(false);
  }, [selectedDate]);

  useEffect(() => {
    if (showNPS) {
      !(function (e, t, r, n) {
        if (!e[n]) {
          for (
            let a = (e[n] = []),
              i = [
                "survey",
                "reset",
                "config",
                "init",
                "set",
                "get",
                "event",
                "identify",
                "track",
                "page",
                "screen",
                "group",
                "alias",
              ],
              s = 0;
            s < i.length;
            s++
          ) {
            let c = i[s];
            a[c] =
              a[c] ||
              (function (e) {
                return function () {
                  let t = Array.prototype.slice.call(arguments);
                  a.push([e, t]);
                };
              })(c);
          }
          a.SNIPPET_VERSION = "1.0.1";
          let o = t.createElement("script");
          (o.type = "text/javascript"),
            (o.async = !0),
            (o.src =
              "https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/" +
              r +
              "/" +
              n +
              ".js");
          let p = t.getElementsByTagName("script")[0];
          p.parentNode.insertBefore(o, p);
        }
      })(window, document, "1QyawY67DwXvYsKQ", "delighted");
      delighted.survey({
        email: user?.email?.trim(),
        name: user?.firstName,
        properties: {
          userId: user?.id,
          address: user?.address,
          employmentType: user?.employmentType,
        },
      });
    }
  }, [showNPS, user]);

  useEffect(() => {
    if (
      (loan?.borrowerSteps?.appraisalVerification?.needRecalculate &&
        !loan?.borrowerFlags?.reAcceptedFinalOffer) ||
      loan?.borrowerSteps?.appraisalVerification?.status === "pending"
    ) {
      setNeedManualAppraisal(true);
    } else {
      setNeedManualAppraisal(false);
    }
  }, [
    loan?.finalOffer?.amount,
    loan?.borrowerFlags?.reAcceptedFinalOffer,
    loan?.borrowerSteps?.appraisalVerification?.needRecalculate,
    loan?.borrowerSteps?.appraisalVerification?.status,
  ]);

  return (
    <CTrackerTimelineItem
      status={
        status === "pending" && disclosuresStatus === "success"
          ? "analyzing"
          : status
      }
      title={isBorrower ? "Select Closing Date" : "Closing Date"}
      tooltip={
        isBorrower
          ? "Pick a day to sign your documents. Please make sure you have access to a computer with a video camera on your scheduled date for best results."
          : "You're all set! The Primary Borrower should now have the option to select a closing date."
      }
      {...(disclosuresStatus === "success" &&
        status !== "success" &&
        !needManualAppraisal &&
        isBorrower && {
          action: {
            message: "Pick a date",
            onClick: () => setCalendarIsOpen(true),
          },
        })}
    >
      <Typography
        variant="h6"
        color={
          color[
            trackerStatusColor[
              needManualAppraisal ? "analyzing" : status
            ] as keyof typeof color
          ]
        }
      >
        {disclosuresStatus === "success" &&
          needManualAppraisal &&
          "Before proceeding, we need to make an appraisal. this may take 1 to 2 days."}
        {status === "analyzing" && "Analyzing"}
        {status === "failed" && message}
        {status === "success" && message}
      </Typography>
      <CDialog
        maxWidth="xs"
        open={calendarIsOpen}
        onClose={() => setCalendarIsOpen(false)}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            fontFamily="Pro Display Semibold"
            fontSize={30}
            whiteSpace="nowrap"
            textAlign="left"
          >
            How Does Closing Work?
          </Typography>
          <IconButton onClick={() => setCalendarIsOpen(false)}>
            <Close fontSize="large" />
          </IconButton>
        </Box>
        <Box justifyContent="flex-start" display="flex" width={150} my={3}>
          <img
            alt="Notarize Logo"
            src={NotarizeLogo}
            style={{ width: "100%", transform: "translateX(-5px)" }}
          />
        </Box>
        <Typography textAlign="left" fontFamily="Pro Text Light">
          <strong>NFTYDoor</strong> is partnered with the #1 eClosing platform,
          Notarize. <strong>Once you select a date for closing</strong> Notarize
          and NFTYDoor will send you an email to access the digital closing
          room. If you need to change the date, just let us know.
        </Typography>
        <CCalendar
          date={selectedDate}
          excludeHollidays
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          onChange={(newDate) => setSelectedDate(newDate)}
          sx={{ width: 350, mx: "auto", mt: 3 }}
        />
        <CButton
          fullWidth
          disabled={disableSubmit}
          variant="contained"
          sx={{ marginTop: 6 }}
          onClick={handleSelectConfirm}
        >
          Confirm
        </CButton>
      </CDialog>
      <CDialog
        maxWidth="xs"
        title={`Your closing date is set for ${selectedDate?.toDateString()}`}
        description="Your closing documents will be generated with this date in mind. Please sign upon receipt. You will recieve your closing documents by email shortly.
        On the day of closing - You and other signing parties will be able to login to Notarize.com at any time that day. You will connect with a Notary and sign the documents that require notarization. This will complete your signings for this loan."
        open={confirm}
        onClose={() => setConfirm(false)}
      >
        <CButton
          fullWidth
          disabled={disableSubmit}
          loading={schedulingDate}
          onClick={scheduleDate}
          variant="contained"
          sx={{ marginBottom: 3, marginTop: 1 }}
        >
          Got it
        </CButton>
      </CDialog>
    </CTrackerTimelineItem>
  );
};

export default ScheduleClosingVerification;
